import React, { useCallback, useMemo, useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, UploadCloudIcon, DownloadIcon, TrashIcon, AlertCircleIcon, CheckCircleIcon, FileSpreadsheetIcon } from 'lucide-react';
const TEMPLATE_COLUMNS = [{
  key: 'supplierName',
  label: 'Supplier Name',
  hint: 'Company or individual name',
  required: true
}, {
  key: 'supplierEmail',
  label: 'Supplier Email',
  hint: 'Contact email',
  required: true
}, {
  key: 'billNumber',
  label: 'Bill Number',
  hint: 'e.g. INV-2026-001',
  required: true
}, {
  key: 'amount',
  label: 'Amount',
  hint: 'Numeric value',
  required: true
}, {
  key: 'currency',
  label: 'Currency',
  hint: 'e.g. THB',
  required: false
}, {
  key: 'dueDate',
  label: 'Due Date',
  hint: 'YYYY-MM-DD',
  required: true
}, {
  key: 'issueDate',
  label: 'Issue Date',
  hint: 'YYYY-MM-DD',
  required: false
}, {
  key: 'description',
  label: 'Description',
  hint: 'Bill description',
  required: false
}, {
  key: 'itemDescription',
  label: 'Item Description',
  hint: 'Line item name',
  required: false
}, {
  key: 'itemQuantity',
  label: 'Item Quantity',
  hint: 'Number',
  required: false
}, {
  key: 'itemUnitPrice',
  label: 'Item Unit Price',
  hint: 'Numeric value',
  required: false
}, {
  key: 'notes',
  label: 'Notes',
  hint: 'Additional notes',
  required: false
}];
interface ParsedRow {
  id: string;
  data: Record<string, string>;
  errors: string[];
}
function generateCSVTemplate(): string {
  const header = TEMPLATE_COLUMNS.map(c => c.label).join(',');
  const sampleRow = ['Acme Corp Ltd', 'billing@acmecorp.com', 'INV-2026-020', '45000', 'THB', '2026-04-15', '2026-03-15', 'Monthly service fee', 'Consulting services', '1', '45000', 'Net 30 payment terms'].join(',');
  return `${header}\n${sampleRow}`;
}
function parseCSV(text: string): {
  headers: string[];
  rows: string[][];
} {
  const lines = text.split('\n').filter(l => l.trim() !== '');
  if (lines.length === 0) return {
    headers: [],
    rows: []
  };
  const headers = lines[0].split(',').map(h => h.trim());
  const rows = lines.slice(1).map(line => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  });
  return {
    headers,
    rows
  };
}
function mapHeaderToKey(header: string): string | null {
  const normalized = header.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const col of TEMPLATE_COLUMNS) {
    const colNorm = col.label.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalized === colNorm) return col.key;
    const keyNorm = col.key.toLowerCase();
    if (normalized === keyNorm) return col.key;
  }
  return null;
}
function validateRow(data: Record<string, string>): string[] {
  const errors: string[] = [];
  if (!data.supplierName) errors.push('Supplier Name is required');
  if (!data.supplierEmail) errors.push('Supplier Email is required');
  if (data.supplierEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.supplierEmail)) {
    errors.push('Invalid email format');
  }
  if (!data.billNumber) errors.push('Bill Number is required');
  if (!data.amount) {
    errors.push('Amount is required');
  } else if (isNaN(Number(data.amount)) || Number(data.amount) <= 0) {
    errors.push('Amount must be a positive number');
  }
  if (!data.dueDate) {
    errors.push('Due Date is required');
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(data.dueDate)) {
    errors.push('Due Date must be in YYYY-MM-DD format');
  }
  if (data.issueDate && !/^\d{4}-\d{2}-\d{2}$/.test(data.issueDate)) {
    errors.push('Issue Date must be in YYYY-MM-DD format');
  }
  return errors;
}
export function BulkBillUpload() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'upload' | 'preview' | 'success'>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [parseError, setParseError] = useState('');
  const validRows = useMemo(() => parsedRows.filter(r => r.errors.length === 0), [parsedRows]);
  const errorRows = useMemo(() => parsedRows.filter(r => r.errors.length > 0), [parsedRows]);
  const totalAmount = useMemo(() => validRows.reduce((sum, r) => sum + (Number(r.data.amount) || 0), 0), [validRows]);
  const handleDownloadTemplate = () => {
    const csv = generateCSVTemplate();
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bill_upload_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  const processFile = useCallback((file: File) => {
    setParseError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const {
        headers,
        rows
      } = parseCSV(text);
      if (headers.length === 0 || rows.length === 0) {
        setParseError('The file appears to be empty or has no data rows.');
        return;
      }
      const keyMap: (string | null)[] = headers.map(mapHeaderToKey);
      const unmapped = headers.filter((_, i) => keyMap[i] === null);
      if (unmapped.length > 0 && keyMap.filter(Boolean).length === 0) {
        setParseError(`Could not match any columns. Please use the template format. Unrecognized headers: ${unmapped.join(', ')}`);
        return;
      }
      const parsed: ParsedRow[] = rows.map((values, idx) => {
        const data: Record<string, string> = {};
        keyMap.forEach((key, i) => {
          if (key && values[i] !== undefined) {
            data[key] = values[i];
          }
        });
        return {
          id: `row-${idx}`,
          data,
          errors: validateRow(data)
        };
      });
      setParsedRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  }, []);
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile]);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const removeRow = (id: string) => {
    setParsedRows(prev => prev.filter(r => r.id !== id));
  };
  const handleImport = () => {
    // In a real app, this would POST to an API and create bills
    setStep('success');
  };
  const handleReset = () => {
    setStep('upload');
    setParsedRows([]);
    setFileName('');
    setParseError('');
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  return <div className="max-w-6xl mx-auto space-y-6 pb-12" data-id="element-5829">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-5830">
        <button onClick={() => navigate('/bills')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-5831">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-5832" />
          Back to Bills
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-5833">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-5834">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-5835">
            Bulk Upload Bills
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-5836">
            Upload a CSV file to add multiple bills at once.
          </p>
        </div>

        <div className="p-8" data-id="element-5837">
          {/* ========== UPLOAD STEP ========== */}
          {step === 'upload' && <div className="space-y-8" data-id="element-5838">
              {/* Template download */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-5 flex items-center justify-between" data-id="element-5839">
                <div className="flex items-center" data-id="element-5840">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mr-4" data-id="element-5841">
                    <FileSpreadsheetIcon size={20} className="text-green-700" data-id="element-5842" />
                  </div>
                  <div data-id="element-5843">
                    <p className="text-sm font-medium text-gray-900" data-id="element-5844">
                      Download template
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-5845">
                      Use this CSV template to format your bill data correctly.
                      Required fields are marked.
                    </p>
                  </div>
                </div>
                <button onClick={handleDownloadTemplate} className="flex items-center px-4 py-2 text-sm font-medium text-[#F7941D] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-5846">
                  <DownloadIcon size={16} className="mr-2" data-id="element-5847" />
                  Download CSV
                </button>
              </div>

              {/* Dropzone */}
              <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all ${isDragging ? 'border-[#F7941D] bg-[#F7941D]/5' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'}`} data-id="element-5848">
                <input type="file" accept=".csv,.txt" onChange={handleFileInput} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-5849" />
                <div className="flex flex-col items-center" data-id="element-5850">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${isDragging ? 'bg-[#F7941D]/10' : 'bg-gray-100'}`} data-id="element-5851">
                    <UploadCloudIcon size={28} className={isDragging ? 'text-[#F7941D]' : 'text-gray-400'} data-id="element-5852" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-5853">
                    {isDragging ? 'Drop your file here' : 'Drag and drop your CSV file here'}
                  </p>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-5854">
                    or click to browse
                  </p>
                  <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md" data-id="element-5855">
                    Supported: .csv
                  </span>
                </div>
              </div>

              {parseError && <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg" data-id="element-5856">
                  <AlertCircleIcon size={18} className="text-red-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-5857" />
                  <div data-id="element-5858">
                    <p className="text-sm font-medium text-red-800" data-id="element-5859">
                      Upload error
                    </p>
                    <p className="text-xs text-red-600 mt-0.5" data-id="element-5860">{parseError}</p>
                  </div>
                </div>}

              {/* Field reference */}
              <div data-id="element-5861">
                <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-5862">
                  Expected fields
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-5863">
                  {TEMPLATE_COLUMNS.map(col => <div key={col.key} className="flex items-center text-xs text-gray-600 py-1.5 px-2.5 bg-gray-50 rounded-md border border-gray-100" data-id="element-5864">
                      <span className="font-medium text-gray-800 mr-1" data-id="element-5865">
                        {col.label}
                      </span>
                      {col.required && <span className="text-red-400 text-[10px]" data-id="element-5866">*</span>}
                    </div>)}
                </div>
              </div>
            </div>}

          {/* ========== PREVIEW STEP ========== */}
          {step === 'preview' && <div className="space-y-6" data-id="element-5867">
              {/* Summary bar */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg border border-gray-200 px-5 py-4" data-id="element-5868">
                <div className="flex items-center space-x-6" data-id="element-5869">
                  <div className="flex items-center text-sm" data-id="element-5870">
                    <FileSpreadsheetIcon size={16} className="text-gray-400 mr-2" data-id="element-5871" />
                    <span className="font-medium text-gray-900" data-id="element-5872">
                      {fileName}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm" data-id="element-5873">
                    <span className="text-gray-600" data-id="element-5874">
                      <span className="font-semibold text-gray-900" data-id="element-5875">
                        {parsedRows.length}
                      </span>{' '}
                      rows total
                    </span>
                    <span className="text-green-600" data-id="element-5876">
                      <span className="font-semibold" data-id="element-5877">{validRows.length}</span>{' '}
                      valid
                    </span>
                    {errorRows.length > 0 && <span className="text-red-600" data-id="element-5878">
                        <span className="font-semibold" data-id="element-5879">
                          {errorRows.length}
                        </span>{' '}
                        with errors
                      </span>}
                  </div>
                </div>
                <button onClick={handleReset} className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-5880">
                  Upload different file
                </button>
              </div>

              {/* Total amount banner */}
              {validRows.length > 0 && <div className="bg-[#F7941D]/5 border border-[#F7941D]/20 rounded-lg px-5 py-4 flex items-center justify-between" data-id="element-5881">
                  <span className="text-sm text-gray-700" data-id="element-5882">
                    Total amount across{' '}
                    <span className="font-semibold" data-id="element-5883">{validRows.length}</span>{' '}
                    valid bills
                  </span>
                  <span className="text-lg font-bold text-gray-900" data-id="element-5884">
                    THB {formatCurrency(totalAmount)}
                  </span>
                </div>}

              {/* Error rows */}
              {errorRows.length > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-5885">
                  <div className="flex items-center mb-3" data-id="element-5886">
                    <AlertCircleIcon size={16} className="text-red-500 mr-2" data-id="element-5887" />
                    <span className="text-sm font-medium text-red-800" data-id="element-5888">
                      {errorRows.length} row{errorRows.length > 1 ? 's' : ''}{' '}
                      with validation errors
                    </span>
                  </div>
                  <p className="text-xs text-red-600 mb-3" data-id="element-5889">
                    These rows have issues and will not be imported. Fix them in
                    your file or remove them below.
                  </p>
                  <div className="space-y-2" data-id="element-5890">
                    {errorRows.map(row => <div key={row.id} className="flex items-center justify-between bg-white rounded-md border border-red-200 px-4 py-2.5" data-id="element-5891">
                        <div className="flex-1 min-w-0" data-id="element-5892">
                          <span className="text-sm font-medium text-gray-900 mr-3" data-id="element-5893">
                            {row.data.billNumber || '(no bill number)'}
                          </span>
                          <span className="text-xs text-gray-500 mr-3" data-id="element-5894">
                            {row.data.supplierName || '(no supplier)'}
                          </span>
                          <span className="text-xs text-red-600" data-id="element-5895">
                            {row.errors.join('; ')}
                          </span>
                        </div>
                        <button onClick={() => removeRow(row.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors ml-3" data-id="element-5896">
                          <TrashIcon size={14} data-id="element-5897" />
                        </button>
                      </div>)}
                  </div>
                </div>}

              {/* Preview table */}
              {validRows.length > 0 && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-5898">
                  <div className="overflow-x-auto" data-id="element-5899">
                    <table className="w-full text-left" data-id="element-5900">
                      <thead data-id="element-5901">
                        <tr className="bg-gray-50 border-b border-gray-200" data-id="element-5902">
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-10" data-id="element-5903">
                            #
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-5904">
                            Bill Number
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-5905">
                            Supplier
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-5906">
                            Email
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-5907">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-5908">
                            Currency
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-5909">
                            Due Date
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-5910">
                            Description
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-12" data-id="element-5911"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100" data-id="element-5912">
                        {validRows.map((row, idx) => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-5913">
                            <td className="px-4 py-3 text-xs text-gray-400" data-id="element-5914">
                              {idx + 1}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-5915">
                              {row.data.billNumber || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-5916">
                              {row.data.supplierName || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-5917">
                              {row.data.supplierEmail || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right font-mono" data-id="element-5918">
                              {row.data.amount ? formatCurrency(Number(row.data.amount)) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-5919">
                              {row.data.currency || 'THB'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-5920">
                              {row.data.dueDate || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate" data-id="element-5921">
                              {row.data.description || '-'}
                            </td>
                            <td className="px-4 py-3" data-id="element-5922">
                              <button onClick={() => removeRow(row.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-gray-100 transition-colors" data-id="element-5923">
                                <TrashIcon size={14} data-id="element-5924" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {parsedRows.length === 0 && <div className="text-center py-12 text-gray-500 text-sm" data-id="element-5925">
                  All rows have been removed. Upload a different file to try
                  again.
                </div>}

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-100" data-id="element-5926">
                <button onClick={handleReset} className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-5927">
                  Cancel
                </button>
                <button onClick={handleImport} disabled={validRows.length === 0} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm ${validRows.length > 0 ? 'bg-[#F7941D] hover:bg-[#D97B06] text-white' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-5928">
                  Import {validRows.length} bill
                  {validRows.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>}

          {/* ========== SUCCESS STEP ========== */}
          {step === 'success' && <div className="text-center py-16" data-id="element-5929">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6" data-id="element-5930">
                <CheckCircleIcon size={32} className="text-green-600" data-id="element-5931" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5932">
                Bills imported successfully
              </h2>
              <p className="text-sm text-gray-500 mb-2" data-id="element-5933">
                {validRows.length} bill
                {validRows.length !== 1 ? 's have' : ' has'} been added to your
                account.
              </p>
              <p className="text-sm text-gray-700 font-medium mb-8" data-id="element-5934">
                Total: THB {formatCurrency(totalAmount)}
              </p>
              <div className="flex justify-center gap-3" data-id="element-5935">
                <button onClick={handleReset} className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-5936">
                  Upload more
                </button>
                <button onClick={() => navigate('/bills')} className="px-5 py-2.5 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-5937">
                  Go to Bills
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}